import React, { useEffect, useState } from "react";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";
import InquiryModal from "../components/InquiryModal";

const channels = [
  {
    label: "General enquiries",
    email: "hello@boramoyo.com",
    subject: "General Inquiry",
    note: "Brand, partnerships, press, and general questions.",
  },
  {
    label: "Member support",
    email: "support@boramoyo.com",
    subject: "Support Request",
    note: "Account issues, billing questions, and technical help.",
  },
  {
    label: "Trust & Safety",
    email: "safety@boramoyo.com",
    subject: "Safety Concern",
    note: "Report abuse, harassment, or any safety concern. We respond within two hours.",
  },
  {
    label: "Press & Media",
    id: "press",
    email: "press@boramoyo.com",
    subject: "Press Inquiry",
    note: "Interview requests, media assets, and press inquiries.",
  },
];

// Fields used by the channel compose modal
const COMPOSE_FIELDS = [
  { name: "name",    label: "Your name",    type: "text",     placeholder: "First and last name", required: true },
  { name: "from",    label: "Your email",   type: "email",    placeholder: "you@example.com",     required: true },
  { name: "subject", label: "Subject",      type: "text",     placeholder: "Subject",             required: true },
  { name: "message", label: "Message",      type: "textarea", placeholder: "Your message…",       required: true, rows: 5 },
];

const CAREERS_FIELDS = [
  { name: "name",      label: "Full name",            type: "text",     placeholder: "First and last name",       required: true  },
  { name: "email",     label: "Email address",        type: "email",    placeholder: "you@example.com",           required: true  },
  {
    name: "role",
    label: "Area of interest",
    type: "select",
    required: true,
    options: ["Product Design", "Full-stack Engineering", "Growth & Community", "Trust & Safety", "Other"],
  },
  { name: "portfolio", label: "LinkedIn or portfolio", type: "url",      placeholder: "https://linkedin.com/in/…", required: false },
  { name: "message",   label: "A short message",       type: "textarea", placeholder: "What draws you to BoraMoyo? What would you bring?", required: true, rows: 4 },
];

export default function Contact() {
  useEffect(() => {
    document.title = "Contact — Bora Moyo";
  }, []);

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);

  // compose modal: tracks which channel is open
  const [compose, setCompose] = useState(null); // null | { email, subject, label }
  const [careersOpen, setCareersOpen] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: wire to backend or form service
    setSent(true);
  };

  return (
    <div className="bg-[#05070d] min-h-screen text-white">
      <MarketingNav />

      <section className="pt-40 pb-20 md:pt-52 md:pb-28 px-6 md:px-12">
        <div className="mx-auto max-w-4xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-5">Get in touch</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[1.02] tracking-tight mb-8">
            We're here,<br />
            <span className="italic text-[#D4AF37]">quietly.</span>
          </h1>
          <p className="text-white/60 text-xl max-w-2xl leading-relaxed">
            We try to respond to every message within one business day. For urgent safety matters, we prioritise within two hours.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-6 md:px-12">
        <div className="h-px bg-white/5" />
      </div>

      <section className="py-20 px-6 md:px-12">
        <div className="mx-auto max-w-6xl grid md:grid-cols-[1.2fr_1fr] gap-16">

          {/* Contact form */}
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-8">Send a message</div>
            {sent ? (
              <div className="backdrop-blur-xl bg-[#0C111D]/65 border border-[rgba(74,163,106,0.3)] rounded-2xl p-10 text-center">
                <div className="text-[#4AA36A] text-4xl font-display mb-4">✓</div>
                <div className="font-display text-2xl mb-2">Message received.</div>
                <p className="text-white/55">We'll be in touch shortly. Thank you for reaching out.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {[
                  { name: "name",    label: "Your name",     type: "text",  placeholder: "First and last name" },
                  { name: "email",   label: "Email address", type: "email", placeholder: "you@example.com"     },
                  { name: "subject", label: "Subject",       type: "text",  placeholder: "What is this about?" },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="block text-xs tracking-[0.16em] uppercase text-white/50 mb-2" htmlFor={f.name}>{f.label}</label>
                    <input
                      id={f.name}
                      name={f.name}
                      type={f.type}
                      placeholder={f.placeholder}
                      value={form[f.name]}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl bg-[#0C111D] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs tracking-[0.16em] uppercase text-white/50 mb-2" htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    placeholder="Tell us what's on your mind..."
                    value={form.message}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl bg-[#0C111D] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50 transition-colors resize-none"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#080B14] font-semibold px-7 py-3.5 hover:scale-[1.02] transition-transform"
                >
                  Send message
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </button>
              </form>
            )}
          </div>

          {/* Direct channels */}
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-8">Direct channels</div>
            <div className="space-y-3">
              {channels.map((c) => (
                <div
                  key={c.label}
                  id={c.id || undefined}
                  className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-5 scroll-mt-24"
                >
                  <div className="text-xs tracking-[0.16em] uppercase text-white/50 mb-1">{c.label}</div>
                  <a
                    href={`mailto:${c.email}?subject=${encodeURIComponent(c.subject)}`}
                    className="font-display text-lg text-[#D4AF37] hover:text-[#F2DE94] transition-colors"
                  >
                    {c.email}
                  </a>
                  <p className="text-xs text-white/45 mt-1.5 leading-relaxed">{c.note}</p>
                  <button
                    onClick={() => setCompose({ email: c.email, subject: c.subject, label: c.label })}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[rgba(212,175,55,0.35)] text-[#F2DE94] text-xs px-4 py-2 hover:bg-[rgba(212,175,55,0.08)] transition-colors"
                  >
                    Compose message →
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAREERS */}
      <section id="careers" className="py-20 border-t border-white/5 px-6 md:px-12 scroll-mt-24">
        <div className="mx-auto max-w-6xl grid md:grid-cols-[1fr_1.4fr] gap-16 items-start">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Careers</div>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-6">
              Join us in building something{" "}
              <span className="italic text-[#D4AF37]">different.</span>
            </h2>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              We're a small, focused team working on a harder problem than it
              looks. We're not trying to build the biggest dating app — we're
              trying to build the most intentional one.
            </p>
            <p className="text-white/60 text-sm leading-relaxed">
              If that sounds like your kind of challenge, we'd like to hear from
              you. Send your background and what draws you to BoraMoyo.
            </p>
          </div>
          <div className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-8">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] mb-6">
              Open to hearing from
            </div>
            <ul className="space-y-4 mb-8">
              {[
                { role: "Product Design",        note: "Dark-mode, interaction-heavy, premium SaaS experience." },
                { role: "Full-stack Engineering", note: "React, Node, or Python. Care about craft."             },
                { role: "Growth & Community",     note: "Building trust with an intentional audience."          },
                { role: "Trust & Safety",         note: "Online safety, moderation, policy."                    },
              ].map((r) => (
                <li key={r.role} className="flex items-start gap-4">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D4AF37] flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-white">{r.role}</div>
                    <div className="text-xs text-white/45 mt-0.5">{r.note}</div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="space-y-3">
              <button
                onClick={() => setCareersOpen(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#080B14] font-semibold px-6 py-3 text-sm hover:scale-[1.02] transition-transform"
              >
                Apply now
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </button>
              <p className="text-xs text-white/30">
                or email{" "}
                <a href="mailto:careers@boramoyo.com" className="text-white/50 hover:text-white/70 transition-colors">
                  careers@boramoyo.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compose modal — shared across all 4 channel cards */}
      <InquiryModal
        isOpen={!!compose}
        onClose={() => setCompose(null)}
        title={compose ? `Message ${compose.label}` : ""}
        recipient={compose ? compose.email : ""}
        fields={COMPOSE_FIELDS}
        defaultValues={compose ? { subject: compose.subject } : {}}
        submitLabel="Send message"
      />

      {/* Careers application modal */}
      <InquiryModal
        isOpen={careersOpen}
        onClose={() => setCareersOpen(false)}
        title="Work with us"
        subtitle="We review every application. If there's a fit, we'll reach out directly."
        fields={CAREERS_FIELDS}
        submitLabel="Submit application"
      />

      <Footer />
    </div>
  );
}
